// High-score panel. Reads GET /api/highscores; the board is shaped entirely
// server-side -- guests, host bots and a player's own weaker runs never reach
// the client -- so the page cannot be edited into showing them.
// A 1v1 has exactly one opponent, so the plural reads wrong there. Keyed off the
// participant count rather than the board, so a row is labelled correctly
// wherever it is rendered. "Mixed" needs two opponents of different kinds and so
// cannot occur in a 1v1 at all.
const OPPOSITION_LABELS = {
  bots: { one: 'vs Bot', many: 'vs Bots' },
  humans: { one: 'vs Human', many: 'vs Humans' },
  mixed: { one: 'Mixed', many: 'Mixed' }
};

function oppositionLabel(opposition, playerCount) {
  const labels = OPPOSITION_LABELS[opposition];
  if (!labels) return '';
  const opponents = Math.max(0, (Number(playerCount) || 0) - 1);
  return opponents === 1 ? labels.one : labels.many;
}

// What the row's chip says. A solo run has no opposition to name, so the chip
// answers the only question left about it: who was steering. The server only
// lists a bot-driven run where that is admissible (solo), so this is where such
// a row declares itself rather than passing as hand-played.
function rowLabel(entry) {
  if (entry?.control === 'self_bot') return { text: 'Self-Bot', selfBot: true };
  // A team row's competitor is the line-up, so "vs Humans" would be saying the
  // obvious. The useful fact is the shape of the match it was set in.
  if (Array.isArray(entry?.members) && entry.members.length > 0) {
    const perSide = Math.max(1, Math.round((Number(entry.playerCount) || 0) / 2));
    return { text: `${perSide}v${perSide}`, selfBot: false };
  }
  return { text: oppositionLabel(entry?.opposition, entry?.playerCount), selfBot: false };
}

const MEDALS = ['gold', 'silver', 'bronze'];

// The boards the server will serve, keyed by the tabs' data-mode.
const MODES = new Set(['solo', '1v1', 'ffa', 'teams']);
const DEFAULT_MODE = '1v1';

const els = {};
let loading = false;
let mode = DEFAULT_MODE;

function cache() {
  if (els.cached) return els;
  els.modal = document.getElementById('highscores-modal');
  els.openBtn = document.getElementById('lobby-highscores-btn');
  els.closeBtn = document.getElementById('highscoresCloseBtn');
  els.modes = [...document.querySelectorAll('.highscores-mode')];
  els.rows = document.getElementById('highscoresRows');
  els.empty = document.getElementById('highscoresEmpty');
  els.cached = true;
  return els;
}

function renderRows(scores) {
  const list = els.rows;
  if (!list) return;
  list.textContent = '';
  if (!scores.length) {
    if (els.empty) els.empty.hidden = false;
    return;
  }
  if (els.empty) els.empty.hidden = true;

  scores.forEach((entry, index) => {
    const row = document.createElement('li');
    row.className = 'highscore-row';
    if (index < MEDALS.length) row.classList.add(`is-${MEDALS[index]}`);

    const rank = document.createElement('span');
    rank.className = 'highscore-rank';
    rank.textContent = String(index + 1);

    const name = document.createElement('span');
    name.className = 'highscore-name';
    // textContent, never innerHTML: player names are untrusted input.
    name.textContent = entry.name || '';

    const score = document.createElement('span');
    score.className = 'highscore-score';
    score.textContent = String(entry.score ?? 0);

    // A hand-played solo row has nothing to say here, and the chip is a bordered
    // pill -- built unconditionally it renders as an empty capsule next to the
    // score. So the chip's presence is itself the signal on the solo board: only
    // a bot-driven run carries one.
    const label = rowLabel(entry);
    if (label.text) {
      const against = document.createElement('span');
      against.className = label.selfBot ? 'highscore-against is-selfbot' : 'highscore-against';
      against.textContent = label.text;
      row.append(rank, name, against, score);
    } else {
      row.append(rank, name, score);
    }
    list.appendChild(row);
  });
}

async function refresh() {
  cache();
  if (loading) return;
  loading = true;
  try {
    const query = new URLSearchParams({ mode, limit: '50' });
    const response = await fetch(`/api/highscores?${query}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`highscores request failed: ${response.status}`);
    const payload = await response.json();
    renderRows(Array.isArray(payload?.scores) ? payload.scores : []);
  } catch (_) {
    renderRows([]);
  } finally {
    loading = false;
  }
}

function setMode(next) {
  const wanted = MODES.has(next) ? next : DEFAULT_MODE;
  if (wanted === mode) return;
  mode = wanted;
  for (const button of els.modes) {
    const active = button.dataset.mode === mode;
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-selected', active ? 'true' : 'false');
  }
  refresh();
}

function openHighScores() {
  cache();
  if (!els.modal) return;
  els.modal.classList.remove('hidden');
  els.modal.setAttribute('aria-hidden', 'false');
  refresh();
}

function closeHighScores() {
  cache();
  if (!els.modal) return;
  els.modal.classList.add('hidden');
  els.modal.setAttribute('aria-hidden', 'true');
}

function initHighScores() {
  cache();
  els.openBtn?.addEventListener('click', openHighScores);
  els.closeBtn?.addEventListener('click', closeHighScores);
  // Click the backdrop (but not the card) to dismiss.
  els.modal?.addEventListener('click', (event) => {
    if (event.target === els.modal) closeHighScores();
  });
  for (const button of els.modes) {
    button.addEventListener('click', () => setMode(button.dataset.mode));
  }
}

export { initHighScores, openHighScores, closeHighScores, refresh as refreshHighScores };
